import React from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { FaAngleUp, FaAngleDown } from "react-icons/fa";
import PaginationComponent from "../PaginationComponent";
import { MdDelete } from "react-icons/md";
import { useAuth } from "../../context/AuthContext"; // Импортируем контекст аутентификации
import PropTypes from "prop-types";

const HistoryModal = ({
  showHistoryModal,
  closeHistoryModal,
  dateInputs,
  setDateInputs,
  setFilterDates,
  filterDates,
  setFilteredPayments,
  paymentHistory,
  currentPayments,
  sortPayments,
  paymentsSortField,
  paymentsSortOrder,
  unmarkAsPaid,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
}) => {
  const { user } = useAuth() || {}; // Получаем роль и уникальный ID пользователя из контекста
  const fmtCurrency = (value) =>
    new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
      Number(value) || 0
    );
  const totalPayoutSum = paymentHistory.reduce(
    (acc, payment) => acc + Number(payment.payout_sum ?? payment.order_amount ?? 0),
    0
  );

  return (
    <Modal
      show={showHistoryModal}
      onHide={closeHistoryModal}
      size="xl"
      centered
      dialogClassName="history-modal"
    >
      <Modal.Header closeButton className="history-modal__header">
        <div>
          <p className="history-eyebrow">Выплаты</p>
          <h3 className="history-title">История выплат</h3>
        </div>
        <div className="history-tags">
          <span className="history-tag">Операций {paymentHistory.length}</span>
          <span className="history-tag history-tag--accent">{fmtCurrency(totalPayoutSum)} EUR</span>
        </div>
      </Modal.Header>
      <Modal.Body className="history-modal__body">
        <Form className="history-filters">
          <div className="history-filters__inputs">
            <Form.Group controlId="fromDate">
              <Form.Label>От</Form.Label>
              <Form.Control
                type="date"
                value={dateInputs.from}
                onChange={(e) => {
                  const fromDate = e.target.value;
                  setFilterDates((prev) => ({ ...prev, from: fromDate }));
                  setDateInputs((prev) => ({ ...prev, from: fromDate }));
                }}
              />
            </Form.Group>
            <Form.Group controlId="toDate">
              <Form.Label>До</Form.Label>
              <Form.Control
                type="date"
                value={dateInputs.to}
                onChange={(e) => {
                  const toDate = e.target.value;
                  setFilterDates((prev) => ({ ...prev, to: toDate }));
                  setDateInputs((prev) => ({ ...prev, to: toDate }));
                }}
              />
            </Form.Group>
          </div>
          <div className="history-filters__actions">
            <Button
              className="btn-ghost"
              onClick={() => {
                const { from, to } = filterDates;
                if (from || to) {
                  setFilteredPayments(
                    paymentHistory.filter((payment) => {
                      const paymentDate = new Date(payment.payment_date).toISOString().split("T")[0];
                      const fromCondition = from ? paymentDate >= from : true;
                      const toCondition = to ? paymentDate <= to : true;
                      return fromCondition && toCondition;
                    })
                  );
                } else {
                  setFilteredPayments(paymentHistory);
                }
                setCurrentPage(1);
              }}
            >
              Применить
            </Button>
            <Button
              variant="link"
              className="history-reset"
              onClick={() => {
                setFilteredPayments(paymentHistory);
                setFilterDates({ from: "", to: "" });
                setDateInputs({ from: "", to: "" });
              }}
            >
              Сбросить
            </Button>
          </div>
        </Form>

        <div className="history-table-wrap">
          <Table responsive className="history-table mb-3">
            <thead>
              <tr>
                <th onClick={() => sortPayments("payment_date")}>
                  Дата оплаты{" "}
                  {paymentsSortField === "payment_date" &&
                    (paymentsSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th onClick={() => sortPayments("order_date")}>
                  Дата создания сделки{" "}
                  {paymentsSortField === "order_date" &&
                    (paymentsSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th onClick={() => sortPayments("order_amount")}>
                  Сумма{" "}
                  {paymentsSortField === "order_amount" &&
                    (paymentsSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th onClick={() => sortPayments("currency")}>
                  Валюта{" "}
                  {paymentsSortField === "currency" &&
                    (paymentsSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                {user?.roles?.includes("admin") && <th></th>}
              </tr>
            </thead>
            <tbody>
              {currentPayments.map((payment, index) => (
                <tr key={`${payment.order_id || "payment"}-${index}`}>
                  <td>{payment.payment_date}</td>
                  <td>{payment.order_date}</td>
                  <td>{fmtCurrency(payment.payout_sum ?? payment.order_amount)}</td>
                  <td>{payment.currency || "—"}</td>
                  {user?.roles?.includes("admin") && (
                    <td className="text-end">
                      <Button
                        variant="link"
                        className="history-delete"
                        onClick={() => unmarkAsPaid(payment.order_id)}
                        title="Удалить выплату"
                      >
                        <MdDelete />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <PaginationComponent
          currentPage={currentPage}
          totalItems={paymentHistory.length}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(items) => setItemsPerPage(items)}
        />
      </Modal.Body>
      <Modal.Footer className="history-modal__footer">
        <Button variant="light" className="btn-ghost" onClick={closeHistoryModal}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

HistoryModal.propTypes = {
  showHistoryModal: PropTypes.bool.isRequired,
  closeHistoryModal: PropTypes.func.isRequired,
  dateInputs: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  setDateInputs: PropTypes.func.isRequired,
  setFilterDates: PropTypes.func.isRequired,
  filterDates: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  setFilteredPayments: PropTypes.func.isRequired,
  paymentHistory: PropTypes.array.isRequired,
  currentPayments: PropTypes.array.isRequired,
  sortPayments: PropTypes.func.isRequired,
  paymentsSortField: PropTypes.string.isRequired,
  paymentsSortOrder: PropTypes.string.isRequired,
  unmarkAsPaid: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  setItemsPerPage: PropTypes.func.isRequired,
};

export default HistoryModal;
